import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL, HAS_API_URL } from '../config/api';
import {
  getTrackedPurchaseIds,
  markPurchaseTracked,
  trackEvent,
} from '../utils/analytics';

/**
 * @param {import('firebase/auth').User} user
 * @returns {Promise<Array<{
 *   transaction_id: string;
 *   pack_name?: string;
 *   value?: number | null;
 *   currency?: string;
 *   credits_spent?: number | null;
 * }>>}
 */
// Failures here (e.g. 500) are unrelated to CubiCasa floor plan parsing; never block upload flows.
async function fetchConfirmedPurchases(user) {
  if (!HAS_API_URL || !user) return [];
  const idToken = await user.getIdToken();
  const res = await fetch(`${API_BASE_URL}/api/polar/confirmed-purchases`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) return [];
  let body = null;
  try {
    body = await res.json();
  } catch {
    return [];
  }
  return Array.isArray(body?.purchases) ? body.purchases : [];
}

/**
 * @param {Array<{
 *   transaction_id: string;
 *   pack_name?: string;
 *   value?: number | null;
 *   currency?: string;
 *   credits_spent?: number | null;
 * }>} purchases
 */
function processPurchases(purchases) {
  const tracked = getTrackedPurchaseIds();
  for (const purchase of purchases) {
    const orderId =
      typeof purchase.transaction_id === 'string' ? purchase.transaction_id.trim() : '';
    if (!orderId || tracked.has(orderId)) continue;
    trackEvent('purchase', {
      transaction_id: orderId,
      pack_name: purchase.pack_name,
      value: purchase.value,
      currency: purchase.currency,
      credits_spent: purchase.credits_spent,
    });
    markPurchaseTracked(orderId);
    tracked.add(orderId);
  }
}

export function usePurchaseTracking() {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pollRef = useRef(null);
  const paymentParam = searchParams.get('payment');

  useEffect(() => {
    if (!user || !HAS_API_URL) return undefined;

    let cancelled = false;

    async function check() {
      if (cancelled) return;
      try {
        const purchases = await fetchConfirmedPurchases(user);
        if (!cancelled) processPurchases(purchases);
      } catch {
        /* ignore network errors */
      }
    }

    void check();

    const paymentSuccess =
      location.pathname === '/credits' && paymentParam === 'success';

    if (paymentSuccess) {
      let elapsed = 0;
      const intervalMs = 3000;
      const maxMs = 90000;
      pollRef.current = window.setInterval(() => {
        elapsed += intervalMs;
        void check();
        if (elapsed >= maxMs && pollRef.current) {
          window.clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, intervalMs);
    }

    return () => {
      cancelled = true;
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [user, location.pathname, paymentParam]);
}
